/**
 * Deep comparison utility for canvas data to detect actual changes
 * Compares initial API page data (State A) with current canvas data (State B)
 * Only considers property changes, not selection/interaction state
 */

/**
 * Normalize a shape object for comparison by removing transient/selection properties
 * @param {Object} shape - Shape object to normalize
 * @returns {Object} Normalized shape object
 */
const normalizeShapeForComparison = (shape) => {
    if (!shape) return null;

    const {
        // Remove transient properties that don't indicate actual changes
        selectedContent,
        isSelected,
        isDragging,
        isHovered,
        isTransforming,
        // Remove runtime properties added during rendering
        canvas_page_location_and_size,
        _lastUpdated,
        // Remove image downscaling properties (these are automatic optimizations, not user changes)
        downscaledSrc,
        downscaledWidth,
        downscaledHeight,
        isDownscaled,
        originalSrc,
        // Keep all other properties for comparison
        ...rest
    } = shape;

    // Normalize undefined vs missing properties
    // Remove properties that are explicitly undefined (treat as missing)
    const normalized = {};
    for (const [key, value] of Object.entries(rest)) {
        if (value !== undefined) {
            normalized[key] = value;
        }
    }

    return normalized;
};

/**
 * Compare two arrays of shapes to detect actual changes
 * @param {Array} arrayA - Initial array (State A)
 * @param {Array} arrayB - Current array (State B)
 * @param {string} arrayType - Type of array for logging
 * @returns {boolean} True if arrays have actual differences
 */
const compareShapeArrays = (arrayA, arrayB, arrayType = 'unknown') => {
    // Quick check: different lengths means changes
    if (arrayA.length !== arrayB.length) {
        return true;
    }

    // If both empty, no changes
    if (arrayA.length === 0) {
        return false;
    }

    // Create maps for efficient lookup by ID
    const mapA = new Map();
    const mapB = new Map();

    arrayA.forEach((shape) => {
        const normalized = normalizeShapeForComparison(shape);
        if (normalized && normalized.id) {
            mapA.set(normalized.id, normalized);
        }
    });

    arrayB.forEach((shape) => {
        const normalized = normalizeShapeForComparison(shape);
        if (normalized && normalized.id) {
            mapB.set(normalized.id, normalized);
        }
    });

    // Check if all IDs match
    if (mapA.size !== mapB.size) {
        return true;
    }

    // Compare each shape's properties
    for (const [id, shapeA] of mapA.entries()) {
        const shapeB = mapB.get(id);

        if (!shapeB) {
            // Shape exists in A but not in B
            return true;
        }

        // Compare all properties (deep comparison)
        if (!deepCompareObjects(shapeA, shapeB)) {
            return true;
        }
    }

    return false;
};

/**
 * Deep compare two objects for equality
 * @param {Object} objA - First object
 * @param {Object} objB - Second object
 * @returns {boolean} True if objects are equal
 */
const deepCompareObjects = (objA, objB) => {
    // Handle null/undefined
    if (objA === objB) return true;
    if (!objA || !objB) return false;
    if (typeof objA !== 'object' || typeof objB !== 'object') return objA === objB;

    // Get all keys from both objects
    const keysA = Object.keys(objA);
    const keysB = Object.keys(objB);

    // Different number of keys
    if (keysA.length !== keysB.length) return false;

    // Compare each key
    for (const key of keysA) {
        if (!keysB.includes(key)) return false;

        const valueA = objA[key];
        const valueB = objB[key];

        // Handle arrays
        if (Array.isArray(valueA) && Array.isArray(valueB)) {
            if (valueA.length !== valueB.length) return false;
            for (let i = 0; i < valueA.length; i++) {
                if (!deepCompareObjects(valueA[i], valueB[i])) return false;
            }
            continue;
        }

        // Handle nested objects
        if (typeof valueA === 'object' && typeof valueB === 'object') {
            if (!deepCompareObjects(valueA, valueB)) return false;
            continue;
        }

        // Handle primitives
        if (valueA !== valueB) return false;
    }

    return true;
};

/**
 * Compare initial API page data with current canvas data to detect actual changes
 * @param {Object} initialState - Initial state (State A) from API
 * @param {Object} currentState - Current state (State B) from canvas
 * @returns {boolean} True if there are actual changes
 */
export const hasActualCanvasChanges = (initialState, currentState) => {
    if (!initialState || !currentState) {
        // If we don't have initial state, assume no changes (conservative approach)
        return false;
    }

    // Compare canvas height
    const initialHeight = parseInt(initialState.canvasHeight) || 100;
    const currentHeight = parseInt(currentState.canvasHeight) || 100;
    if (initialHeight !== currentHeight) {
        return true;
    }

    // Compare each shape array
    const shapeTypes = [
        'texts',
        'rectangles',
        'circles',
        'arrows',
        'lines',
        'doubleSidedArrows',
        'scribbles',
        'images',
        'videos'
    ];

    for (const type of shapeTypes) {
        const initialArray = initialState[type] || [];
        const currentArray = currentState[type] || [];

        if (compareShapeArrays(initialArray, currentArray, type)) {
            return true;
        }
    }

    return false;
};

/**
 * Create a snapshot of canvas state for comparison
 * Removes transient properties to create a clean baseline
 * @param {Object} state - Canvas state object
 * @returns {Object} Normalized state snapshot
 */
export const createCanvasStateSnapshot = (state) => {
    if (!state) return null;

    const snapshot = {
        canvasHeight: parseInt(state.canvasHeight) || 100,
    };

    const shapeTypes = [
        'texts',
        'rectangles',
        'circles',
        'arrows',
        'lines',
        'doubleSidedArrows',
        'scribbles',
        'images',
        'videos'
    ];

    for (const type of shapeTypes) {
        const array = state[type] || [];
        snapshot[type] = array.map(normalizeShapeForComparison).filter(Boolean);
    }

    return snapshot;
};
